import type { ActionType, ClientApiApi as AppClient, Interaction, User } from "@autointel/client-api";
import { DeviceType } from "@autointel/whiz-models";

function isChildElementOfWhizChat(element: HTMLElement): boolean {
    if ("whiz-chat" === element.id) {
        return true;
    }

    if (!element.parentElement) {
        return false;
    }

    return isChildElementOfWhizChat(element.parentElement);
}

export default class InteractionsListener {
    _app?: AppClient;
    _user?: User;
    unsentInteractions: Interaction[] = [];

    get app(): AppClient | undefined {
        return this._app;
    }

    set app(value: AppClient | undefined) {
        this._app = value;
        if (this._app && this._user) {
            this.sendUnsentInteractions();
        }
    }

    get user(): User | undefined {
        return this._user;
    }

    set user(value: User | undefined) {
        this._user = value;
        if (this._app && this._user) {
            this.sendUnsentInteractions();
        }
    }

    constructor() {
        this.attachPageListenerForInteractions();
    }

    sendUnsentInteractions() {
        if (!this.app || !this.user) {
            console.warn("SDK not initialized");
            return;
        }

        this.unsentInteractions.forEach((interaction) => {
            this.app!.postInteraction({
                interaction: {
                    ...interaction,
                    context: {
                        ...interaction.context,
                        browsingSession: {
                            id: `${this.user!.id}-${interaction.timestamp}`,
                            ...((window.navigator as any).userAgentData?.mobile && {
                                deviceType: DeviceType.MOBILE,
                            }),
                        },
                        user: {
                            ...this.user,
                            id: this.user!.id,
                            userAgent: window.navigator.userAgent,
                        },
                    },
                },
            });
        });
    }

    async attachPageListenerForInteractions(tagNames: string[] = ["a", "button", "div", "span", "video"]) {
        if (typeof window === "undefined") {
            return;
        }

        document.body.addEventListener("mousedown", (event) => {
            const target = event.target as HTMLElement | undefined;
            if (target) {
                const { tagName } = target;
                const timestamp = Date.now();
                if (tagNames.includes(tagName?.toLowerCase()) && !isChildElementOfWhizChat(target)) {
                    const interaction: Interaction = {
                        itemId: `${window.location.pathname}-body-${tagName}`,
                        context: {
                            browsingSession: {
                                id: `${this.user?.id}-${timestamp}`,
                                ...((window.navigator as any).userAgentData?.mobile && {
                                    deviceType: DeviceType.MOBILE,
                                }),
                            },
                            user: {
                                ...this.user,
                                id: this.user?.id ?? "",
                                userAgent: window.navigator.userAgent,
                            },
                            source: {
                                url: window.location.href,
                                name: document.title,
                            },
                            attributes: {
                                textContent: target.textContent,
                                ...Object.fromEntries(
                                    Object.values(target.attributes).map((attr) => [attr.name, attr.value]),
                                ),
                            },
                        },
                        action: "SELECT" as ActionType,
                        timestamp,
                    };

                    if (!this.app || !this.user) {
                        console.warn("SDK not initialized");
                        this.unsentInteractions.push(interaction);
                        return;
                    }

                    this.app.postInteraction({
                        interaction,
                    });
                }
            }
        });
    }
}
